import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { apiClient } from '../../apiClient'

interface ProgramPerformanceOverview {
  programId: string
  name: string | null
  operatorName: string | null
  code: string
  icon: string | null
  color: string | null
  unreadCount: number
  perWeekAvg: number[]
  unreadCommentsCount: number
  isEligible: boolean
}

export interface ProgramPerformanceOverviewWithLastAvg extends ProgramPerformanceOverview {
  lastAvg: number
}

interface MissionSpecialistPerformanceOverviewResponse {
  userLastLogin: string | null
  programs: ProgramPerformanceOverview[]
}

export const MSPerformanceSummaryKey = 'performance-summary-of-mission-specialist'

async function queryFn() {
  const response = await apiClient.get<MissionSpecialistPerformanceOverviewResponse>(
    '/int/v3/operators/programs/overview'
  )

  return response.data
}

export function usePerformanceSummary() {
  return useQuery({
    queryKey: [MSPerformanceSummaryKey],
    queryFn,
    refetchOnWindowFocus: true,
    select(data) {
      const active = data.programs.filter((p) => p.isEligible === true).map(addLastAvgToOverview)
      const inactive = data.programs.filter((p) => p.isEligible === false).map(addLastAvgToOverview)

      return {
        active,
        inactive,
      }
    },
  })
}

export function useIsEligibleForProgram(programId: string | undefined) {
  const { data } = usePerformanceSummary()
  const isEligible = useMemo(() => {
    if (!programId) {
      return false
    }

    return data?.active?.find((p) => p.programId === programId)?.isEligible ?? false
  }, [data, programId])

  return isEligible
}

const addLastAvgToOverview = (p: ProgramPerformanceOverview): ProgramPerformanceOverviewWithLastAvg => ({
  ...p,
  lastAvg: p.perWeekAvg[0],
})
